import { Button, Icon } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Popover2 } from "@blueprintjs/popover2";
import { FormikProps, getIn } from "formik";
import moment from "moment";
import {
  FGCheckboxInput,
  FGCustomInput,
  FGListen,
  FGNumberInput,
  FGSelectInput,
  FGTextInput,
  FieldGroup,
  showError,
  showSuccess,
} from "nsitools-react";
import * as React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

import {
  BroodRequestApi,
  DogBroodRequestsTransientDto,
  DogBroodRequestTransientOwnerGridDto,
  EDogSex,
  EKennelType,
  ELabStatus,
  EPlatform,
  ErrorDetails,
} from "../../../../api";
import { FGDogSearchInput } from "../../../../components";
import { useAuth, useDialog, useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useTl, useWindow } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";
import { PuppyOwners } from "./PuppyOwners";

const Container = styled.div`
  display: flex;
`;

const StyledUppperCamelCaseContainer = styled.div`
  * > * {
    text-transform: capitalize;
  }
`;

export interface IPuppyCardProps {
  index: number;
  puppy: DogBroodRequestsTransientDto;
  breeds: any;
  pedigrees: any;
  refresh: any;
  platform?: EPlatform;
  yearLetter: string;
  isLoading?: boolean;
  tooSmall?: boolean;
}

export const PuppyCard: React.FunctionComponent<IPuppyCardProps> = ({
  index,
  puppy,
  breeds,
  refresh,
  pedigrees,
  platform = EPlatform.CARE4DOGS,
  yearLetter,
  isLoading,
  tooSmall,
}) => {
  const { t, tUnsafe } = useTl();
  const api = useApiService(BroodRequestApi);
  const { userPermissions } = useAuth();
  const { showDialogPromise } = useDialog();

  const [resurrectPuppyLoading, setresurrectPuppyLoading] = React.useState(false);

  const ressurectPuppy = React.useCallback(
    async (id: number) => {
      setresurrectPuppyLoading(true);
      try {
        var res = await showDialogPromise({
          message: t(ETLCodes.WarningMustBeAliveInProgenus),
          title: t(ETLCodes.Warning),
        });
        if (res === "yes" || res === "ok") {
          await api.resurrectPuppy(id);
          showSuccess(t(ETLCodes.SaveSuccess));
          refresh();
        }
      } catch {
        showError(t(ETLCodes.Error));
      } finally {
        setresurrectPuppyLoading(false);
      }
    },
    [api, refresh, showDialogPromise, t]
  );

  const dogExists = React.useMemo(() => puppy.correspondingDogId !== null, [puppy.correspondingDogId]);

  const canCreatePedigree = React.useMemo(
    () =>
      !!userPermissions.find(
        (c) => c.module === "BROODREQUEST" && c.tab === "PUPPIESPEDIGREE" && c.permission === "E"
      ) && !puppy.isCorrespondingDogComplete,
    [puppy.isCorrespondingDogComplete, userPermissions]
  );

  const [createPedigreeLoading, setCreatePedigreeLoading] = React.useState(false);

  const { openNewTab } = useWindow();
  const { currentLanguage } = useGlobalData();
  const [breedId, setBreedId] = React.useState<number>(0);
  const [colors, cLoading] = useReferential((c) => (breedId ? c.getAuthorizedColors(breedId, true) : undefined), true, [
    breedId,
  ]);
  const [sexes, sloading] = useReferential((c) => c.getSex(currentLanguage.languageId));

  const filterValue = React.useMemo(
    () => (puppy.labStatus === ELabStatus.AnalysisDone || puppy.deceased ? "none" : "grayscale(100%)"),
    [puppy.deceased, puppy.labStatus]
  );

  const createPedigree = React.useCallback(async () => {
    try {
      setCreatePedigreeLoading(true);
      const dialogResult = await showDialogPromise({
        message: t(ETLCodes.CreateDogAndPedigreeConfirmationMessage),
        title: t(ETLCodes.CreateDogAndPedigreeConfirmationTitle),
      });
      if (dialogResult !== "yes") return;
      const res = await api.createDogBroodRequestPedigree(puppy.dogBroodRequestsTransientId);
      refresh();
      setCreatePedigreeLoading(false);
      return res;
    } catch (error) {
      console.log("error", error);
      const details: ErrorDetails = JSON.parse(error.response);
      if (details?.errorCode) {
        console.log("custom error");
        showError(tUnsafe(`BackendError_${details.errorCode}`));
      } else {
        console.log("generic error");
        showError(t(ETLCodes.Error));
      }

      setCreatePedigreeLoading(false);
    }
  }, [api, puppy.dogBroodRequestsTransientId, refresh, showDialogPromise, t, tUnsafe]);

  const puppyDeceasedFunc = React.useCallback(async () => {
    try {
      const dialogResult = await showDialogPromise({
        message: t(ETLCodes.PuppyDeceasedConfirmationMessage),
        title: t(ETLCodes.PuppyDeceasedConfirmationTitle),
      });
      if (dialogResult !== "yes") return;
      const res = await api.setPuppyDeceased(puppy.dogBroodRequestsTransientId, platform);
      refresh();
      return res;
    } catch {
      showError(t(ETLCodes.Error));
    }
  }, [api, platform, puppy.dogBroodRequestsTransientId, refresh, showDialogPromise, t]);

  const PuppyName = React.useMemo(
    () => (
      <Container
        style={{
          marginTop: "0.5rem",
          flexDirection: puppy.kennelType === EKennelType.Prefix ? "row" : "row-reverse",
        }}
      >
        {platform === EPlatform.CARE4DOGS ? (
          <Link target="_blank" to={`${ERoutes.kennel}/${puppy?.kennelId}/detail/view`} style={{ marginRight: "1rem" }}>
            {puppy?.kennelName}
          </Link>
        ) : (
          <div style={{ marginTop: "0.75rem" }}>{puppy?.kennelName}</div>
        )}
        <StyledUppperCamelCaseContainer>
          <FGTextInput
            name={`${platform === EPlatform.PORTAL ? "dogBroodRequestGetDto." : ""}puppies[${index}].name`}
            readonly={puppy.deceased || dogExists}
            placeholder={`${t(ETLCodes.CalendarYear)}: ${yearLetter}`}
          />
        </StyledUppperCamelCaseContainer>
      </Container>
    ),
    [index, platform, puppy.deceased, puppy?.kennelId, puppy?.kennelName, puppy.kennelType, dogExists, t, yearLetter]
  );

  const updateListFormik = React.useCallback(
    (newList: DogBroodRequestTransientOwnerGridDto[], formik: FormikProps<any>) => {
      if (platform === EPlatform.CARE4DOGS) {
        formik?.setFieldValue(`puppies[${index}].owners`, newList);
        formik?.setFieldTouched(`puppies[${index}].owners`, true);
      } else {
        formik?.setFieldValue(`dogBroodRequestGetDto.puppies[${index}].owners`, newList);
        formik?.setFieldTouched(`dogBroodRequestGetDto.puppies[${index}].owners`, true);
      }
    },
    [index, platform]
  );

  return (
    <div
      style={{
        border: "1px solid #c2c0c0",
        borderRadius: "25px",
        padding: "1rem",
        marginBottom: "1rem",
        filter: filterValue,
      }}
    >
      <FGListen
        field={`${platform === EPlatform.PORTAL ? "dogBroodRequestGetDto." : ""}puppies[${index}].name`}
        onChanged={(value, formik) => {
          if (value !== null && value.charAt(0) === value.charAt(0).toLowerCase()) {
            formik.setFieldValue(
              `${platform === EPlatform.PORTAL ? "dogBroodRequestGetDto." : ""}puppies[${index}].name`,
              value.charAt(0).toUpperCase() + value.slice(1)
            );
          }
        }}
      />
      <FGListen
        field={`${platform === EPlatform.PORTAL ? "dogBroodRequestGetDto." : ""}puppies[${index}].breedId`}
        onChanged={setBreedId}
      />

      <FieldGroup
        fieldsetProps={{
          title: (
            <div style={puppy?.deceased ? { color: "RED" } : { color: "Black" }}>
              {puppy?.deceased && <i className="fas fa-skull-crossbones" />}
              {` ${
                platform === EPlatform.PORTAL
                  ? t(ETLCodes.Puppy) + "#" + index + 1
                  : t(ETLCodes.ProgenusPuppy) + "-" + puppy.dogBroodRequestsTransientId
              } ${puppy?.chipNumber ? " - " + puppy?.chipNumber : ""}  ${
                puppy?.deceased ? `(${t(ETLCodes.Deceased)})` : ``
              }`}
              - <strong>{puppy.labStatusTranslation + " "}</strong> (
              {!!puppy?.lastLabStatusUpdateDoneOn && moment(puppy?.lastLabStatusUpdateDoneOn).format("DD/MM/YYYY")})
              <Popover2
                content={<div style={{ padding: "0.5rem" }}>{puppy?.labStatusDescriptionTranslation}</div>}
                interactionKind="hover-target"
              >
                <Icon icon="info-sign" intent="primary" iconSize={20} style={{ marginBottom: "0.24rem" }} />
              </Popover2>
              {platform === EPlatform.CARE4DOGS && puppy?.deceased && (
                <Button
                  intent="danger"
                  style={{ marginLeft: "0.5rem" }}
                  text={t(ETLCodes.Resurrect)}
                  loading={resurrectPuppyLoading}
                  onClick={() => ressurectPuppy(puppy.dogBroodRequestsTransientId)}
                />
              )}
            </div>
          ),
        }}
        rightElement={
          puppy?.pedigreeId &&
          puppy?.correspondingDogId &&
          puppy?.labStatus === ELabStatus.AnalysisDone &&
          platform === EPlatform.CARE4DOGS && (
            <Button
              minimal
              text={t(ETLCodes.ViewDogDetail)}
              icon={IconNames.EYE_OPEN}
              onClick={() => openNewTab(`${ERoutes.dog}/${puppy?.correspondingDogId}/detail/view`)}
            />
          )
        }
      >
        {(puppy.labStatus === ELabStatus.AnalysisDone || puppy.deceased) && (
          <>
            <FieldGroup columns={tooSmall ? 1 : 3}>
              <FGSelectInput
                name={`${platform === EPlatform.PORTAL ? "dogBroodRequestGetDto." : ""}puppies[${index}].sex`}
                label={t(ETLCodes.Sex)}
                items={sexes}
                readonly={platform !== EPlatform.CARE4DOGS || dogExists || puppy.deceased}
                loading={sloading}
              />
              <FGCheckboxInput
                name={`${platform === EPlatform.PORTAL ? "dogBroodRequestGetDto." : ""}puppies[${index}].dockedTail`}
                label={t(ETLCodes.PuppyDockedTail)}
              />
              <FGDogSearchInput
                name={`${platform === EPlatform.PORTAL ? "dogBroodRequestGetDto." : ""}puppies[${index}].fatherId`}
                label={t(ETLCodes.Father)}
                defaultSearch={{ sex: EDogSex.Male }}
                readonly
                onItemViewClick={
                  platform === EPlatform.PORTAL ? null : (item) => openNewTab(`${ERoutes.dog}/${item}/detail/view`)
                }
                requiredMark
              />
            </FieldGroup>
            <FGCustomInput label={t(ETLCodes.Name)}>
              {(ctx) => (
                <FieldGroup columns={tooSmall ? [8, 4] : [3, 9]}>
                  {PuppyName}
                  {!puppy?.pedigreeNumber && (
                    <Button disabled={puppy.deceased} intent={"danger"} onClick={puppyDeceasedFunc}>
                      {platform === EPlatform.CARE4DOGS ? t(ETLCodes.Deceased) : t(ETLCodes.ClickHereIfDogDeceased)}
                    </Button>
                  )}
                </FieldGroup>
              )}
            </FGCustomInput>

            {platform === EPlatform.CARE4DOGS && (
              <FieldGroup columns={tooSmall ? 2 : 4}>
                <FGTextInput
                  label={t(ETLCodes.ChipNumber)}
                  name={`puppies[${index}].chipNumber`}
                  readonly={puppy.deceased || dogExists}
                />
                {puppy.isBrcs && (
                  <FGCustomInput name="inbreedingCustomFIeld" label={t(ETLCodes.Inbreeding)}>
                    {(ctx) => (
                      <div style={{ display: "flex" }}>
                        <FGNumberInput noLabel min={0} max={100} name={`puppies[${index}].inbreeding`} />{" "}
                        <div style={{ marginLeft: "0.5rem", marginTop: "5px" }}>/</div>{" "}
                        <FGNumberInput noLabel name={`puppies[${index}].maxExpectedInbreeding`} readonly />
                        {getIn(ctx.formik.values, `puppies[${index}].inbreeding`) <=
                        getIn(ctx.formik.values, `puppies[${index}].maxExpectedInbreeding`) ? (
                          <Icon intent="success" icon="tick" />
                        ) : (
                          <Icon intent="danger" icon="cross" />
                        )}
                      </div>
                    )}
                  </FGCustomInput>
                )}
              </FieldGroup>
            )}

            <FGCustomInput>
              {(ctx) => (
                <PuppyOwners
                  puppy={puppy}
                  index={index}
                  platform={platform}
                  readonly={dogExists}
                  tooSmall={tooSmall}
                  onOwnersChanged={(newOwners) => {
                    console.log(
                      "formik check ",
                      ctx.formik?.values[
                        `${
                          platform === EPlatform.CARE4DOGS
                            ? `puppies[${index}].owners`
                            : `dogBroodRequestGetDto.puppies[${index}].owners`
                        }`
                      ]
                    );
                    updateListFormik(newOwners, ctx.formik);
                    ctx.formik.validateForm();
                    ctx.formik.validateField(
                      `${platform === EPlatform.PORTAL ? "dogBroodRequestGetDto." : ""}puppies[${index}].name`
                    );
                    ctx.formik.validateField(
                      `${platform === EPlatform.PORTAL ? "dogBroodRequestGetDto." : ""}puppies[${index}].name`
                    );
                    ctx.formik.validateField(
                      `${platform === EPlatform.PORTAL ? "dogBroodRequestGetDto." : ""}puppies[${index}].colorId`
                    );
                  }}
                  isError={() => {
                    if (platform === EPlatform.CARE4DOGS) {
                      return (
                        !ctx.formik.values?.puppies[index].deceased &&
                        !ctx.formik.values?.puppies[index].owners?.length &&
                        (!!ctx.formik.values?.puppies[index].name || !!ctx.formik.values?.puppies[index].colorId)
                      );
                    } else {
                      return (
                        !ctx.formik.values?.dogBroodRequestGetDto.puppies[index].deceased &&
                        !ctx.formik.values?.dogBroodRequestGetDto.puppies[index].owners?.length &&
                        (!!ctx.formik.values?.dogBroodRequestGetDto.puppies[index].name ||
                          !!ctx.formik.values?.dogBroodRequestGetDto.puppies[index].colorId)
                      );
                    }
                  }}
                />
              )}
            </FGCustomInput>
            <FieldGroup columns={tooSmall ? 1 : 2}>
              <FGSelectInput
                name={`${platform === EPlatform.PORTAL ? "dogBroodRequestGetDto." : ""}puppies[${index}].breedId`}
                label={t(ETLCodes.Breed)}
                items={breeds}
                readonly={puppy.deceased || dogExists}
                onItemViewClick={(item: number) => openNewTab(`${ERoutes.breeds}/${item}/detail/view`)}
              />
            </FieldGroup>
            <FieldGroup columns={tooSmall ? 1 : 2}>
              <FGSelectInput
                name={`${platform === EPlatform.PORTAL ? "dogBroodRequestGetDto." : ""}puppies[${index}].colorId`}
                label={t(ETLCodes.Colors)}
                items={colors}
                readonly={puppy.deceased || dogExists}
                loading={cLoading}
                disabled={!breedId}
                onItemViewClick={(item: number) => openNewTab(`${ERoutes.color}/${item}/detail/view`)}
              />
            </FieldGroup>
            <FieldGroup columns={tooSmall ? 1 : 2}>
              {platform === EPlatform.CARE4DOGS ? (
                <FGSelectInput
                  name={`puppies[${index}].countryPedigreePrefixId`}
                  label={t(ETLCodes.PedigreePrefix)}
                  items={pedigrees}
                  readonly={puppy.deceased || dogExists}
                />
              ) : null}
            </FieldGroup>
            <FieldGroup columns={tooSmall ? 1 : 2}>
              <FGCheckboxInput
                name={`${platform === EPlatform.PORTAL ? "dogBroodRequestGetDto." : ""}puppies[${index}].available`}
                label={t(ETLCodes.Availability)}
                readonly={puppy.deceased || dogExists}
              />
            </FieldGroup>
            {platform === EPlatform.CARE4DOGS && (
              <FGCustomInput label={t(ETLCodes.Pedigree)}>
                {(ctx) =>
                  puppy.pedigreeId ? (
                    <>{puppy.pedigreeNumber}</>
                  ) : (
                    <Button
                      disabled={
                        !(
                          puppy.breedId &&
                          puppy.name &&
                          puppy.colorId &&
                          puppy.chipNumber &&
                          ((puppy.inbreeding != null && puppy.inbreeding != undefined) || !puppy.isBrcs) &&
                          puppy?.sex &&
                          puppy.fatherId &&
                          !puppy.deceased &&
                          puppy.countryPedigreePrefixId &&
                          puppy.owners.length > 0
                        ) || !canCreatePedigree
                      }
                      loading={createPedigreeLoading || isLoading}
                      onClick={createPedigree}
                      intent={"primary"}
                    >
                      {t(ETLCodes.CreatePedigree)}
                    </Button>
                  )
                }
              </FGCustomInput>
            )}
          </>
        )}
      </FieldGroup>
    </div>
  );
};
